import { Toast } from '@components/Toast';
import { faAngleLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UpdateStepContent } from '@partful/entities/instructions/stepContent';
import { useCreateStepContent } from '@partful/features/instructions/steps/lib/useCreateStepContent';
import { useUpdateStepContent } from '@partful/features/instructions/steps/lib/useUpdateStepContent';
import { useListVersionLanguages } from '@partful/features/instructions/version-languages/lib/useListVersionLanguages';
import {
  SegmentModel,
  SegmentWithContentModel,
  StepModel,
  StepWithContentModel,
} from '@partful/instructions-domain-models';
import { Button } from '@partful/shared/ui/Button';
import { Option, Select } from '@partful/shared/ui/Select';
import { Surface } from '@partful/shared/ui/Surface';
import { Typography } from '@partful/shared/ui/Typography';
import { useCallback } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { StepContentForm } from '../StepContentForm/StepContentForm';
import { StepNotices } from '../StepNotices/StepNotices';
import styles from './StepContent.module.scss';

type StepContentProps = {
  instructionId: string;
  versionId: string;
  segment: SegmentModel & Partial<SegmentWithContentModel>;
  step: StepModel & Partial<StepWithContentModel>;
  languageCode: string;
  onChangeLanguage: (languageCode: string) => void;
};

export function StepContent({
  instructionId,
  versionId,
  segment,
  step,
  languageCode,
  onChangeLanguage,
}: Readonly<StepContentProps>) {
  const { t: i18n } = useTranslation();
  const { goBack } = useHistory();

  const { data: languages } = useListVersionLanguages(instructionId, versionId);

  const {
    mutate: createStepContent,
    isLoading: isCreatingStepContent,
    isSuccess: isSuccessCreatingStepContent,
    isError: isErrorCreatingStepContent,
  } = useCreateStepContent();

  const {
    mutate: updateStepContent,
    isLoading: isUpdatingStepContent,
    isSuccess: isSuccessUpdatingStepContent,
    isError: isErrorUpdatingStepContent,
  } = useUpdateStepContent();

  const handleUpdateStepContent = useCallback<SubmitHandler<UpdateStepContent>>(
    async ({ name, description, tools }) => {
      if (step)
        if (step.content != null) {
          updateStepContent({
            instructionId,
            versionId,
            segmentId: segment.id,
            stepId: step.id,
            languageCode,
            updateArgs: { __expectedVersion: step.content.__version, name, description, tools },
          });
        } else {
          createStepContent({
            instructionId,
            versionId,
            segmentId: segment.id,
            stepId: step.id,
            args: { name, description: description!, tools: tools!, languageCode },
          });
        }
    },
    [step, updateStepContent, instructionId, versionId, segment.id, languageCode, createStepContent]
  );

  return (
    <Surface className={styles.stepContent} shadow="large">
      <div className={styles.header}>
        <div className={styles.navigation}>
          <Button name="instructions_stepeditor_back_click" variant="primary" onClick={goBack}>
            <FontAwesomeIcon icon={faAngleLeft} />
            {i18n('common:backButton')}
          </Button>
          <Select
            value={languages ? languageCode : ''}
            className={styles.language}
            size="small"
            onChange={(e) => onChangeLanguage(e.target.value)}
            data-testid="languages-field"
          >
            {languages?.map((language) => (
              <Option
                key={language.languageCode}
                value={language.languageCode}
                data-testid={`languages-${language.languageCode}-option`}
              >
                {i18n(`languageNames:${language.languageCode}`)}
              </Option>
            ))}
          </Select>
        </div>
        <div className={styles.segment}>
          <Typography appearance="heading5" as="h1" overflow="hidden" textOverflow="ellipsis">
            {segment?.content?.name}
          </Typography>
          <Typography
            as="p"
            overflow="hidden"
            textOverflow="ellipsis"
            title={segment?.content?.description}
            WebkitLineClamp={3}
          >
            {segment?.content?.description}
          </Typography>
        </div>
      </div>
      {step && (
        <div className={styles.step}>
          <StepNotices
            instructionId={instructionId}
            versionId={versionId}
            segmentId={segment.id}
            stepId={step.id}
            languageCode={languageCode}
          />
          <StepContentForm
            content={step.content}
            onSubmit={handleUpdateStepContent}
            disabled={isCreatingStepContent || isUpdatingStepContent}
          />
        </div>
      )}
      <Toast
        dependency={isErrorCreatingStepContent || isErrorUpdatingStepContent}
        severity="error"
        title={i18n('instructions:step.actions.saveError.title')}
        message={i18n('instructions:step.actions.saveError.message')}
        testId="update-step-content-error-message"
      />
      <Toast
        dependency={isSuccessCreatingStepContent || isSuccessUpdatingStepContent}
        severity="success"
        title={i18n('instructions:step.actions.saveSuccess.title')}
        testId="update-step-content-success-message"
      />
    </Surface>
  );
}
